import * as React from 'react';
import { Input } from '@progress/kendo-react-inputs';
import SearchIcon from 'assets/images/ic_search.svg';
import InfoIcon from 'assets/images/ic_nav_info.svg';
import { ic_close_small_black as CloseIcon } from 'app/widgets/SVGs';

import { FaultCodesErrorType } from './FaultCodesErrorTypeTabs';
import { nonDigitRegex } from 'app/common/utils';

type Props = {
  selectedErrorType: FaultCodesErrorType;
  searchValue: string;
  spnSearchValue: string;
  fmiSearchValue: string;
  language: string;
  infoSpnSrc: string;
  infoFmiSrc: string;
  t: (key: string, options?: any) => string;

  onSearchValueChange: (value: string) => void;
  onSearchValueClear: () => void;

  onSpnChange: (value: string) => void;
  onSpnClear: () => void;

  onFmiChange: (value: string) => void;
  onFmiClear: () => void;
};

export function FaultCodesSearchInputs({
  selectedErrorType,
  searchValue,
  spnSearchValue,
  fmiSearchValue,
  language,
  infoSpnSrc,
  infoFmiSrc,
  t,
  onSearchValueChange,
  onSearchValueClear,
  onSpnChange,
  onSpnClear,
  onFmiChange,
  onFmiClear,
}: Props) {
  const anchorSpn = React.useRef<HTMLImageElement | null>(null);
  const anchorFmi = React.useRef<HTMLImageElement | null>(null);

  if (selectedErrorType === 'all') {
    return (
      <div className="search-box">
        <span className="search-with-options">
          <div className="site-search search-with-before-text">
            <span className="search-icon">
              <img alt="Search" src={SearchIcon}></img>
            </span>
            <div className="search-input">
              <Input
                id="searchAll"
                name="searchAll"
                value={searchValue}
                placeholder={t('search_all_fault_codes')}
                onChange={newValue => {
                  onSearchValueChange(newValue.value);
                }}
              />
              {searchValue && (
                <span
                  className="search-icon-close"
                  onClick={onSearchValueClear}
                >
                  <CloseIcon />
                </span>
              )}
            </div>
          </div>
        </span>
      </div>
    );
  }

  if (selectedErrorType === 'dtc') {
    return (
      <div className="search-box">
        <span className="search-with-options">
          <div className="site-search search-with-before-text">
            <span className="search-icon">
              <img alt="Search" src={SearchIcon}></img>
            </span>
            <div className="search-input">
              <span className="before-text">{t('fault_e')}</span>
              <Input
                id="searchECode"
                name="searchECode"
                value={searchValue}
                placeholder={t('search_e_fault_codes')}
                onChange={newValue => {
                  const value = newValue.value ?? '';
                  const digitsOnly = value.trim().replace(nonDigitRegex, '');
                  onSearchValueChange(digitsOnly);
                }}
              />
              {searchValue && (
                <span
                  className="search-icon-close"
                  onClick={onSearchValueClear}
                >
                  <CloseIcon />
                </span>
              )}
            </div>
          </div>
        </span>
      </div>
    );
  }

  // j1939
  return (
    <div>
      <div className="search-box search-box-with-popover">
        <span className="search-with-options">
          <div className="site-search search-with-before-text">
            <span className="search-icon">
              <img alt="Search" src={SearchIcon}></img>
            </span>
            <div className="search-input">
              <span className="before-text">{t('fault_spn')}</span>
              <Input
                id="searchSPN"
                name="searchSPN"
                value={spnSearchValue}
                placeholder={t('search_spn_fault_codes')}
                onChange={newValue => {
                  onSpnChange(newValue.value);
                }}
              />
              {spnSearchValue && (
                <span className="search-icon-close" onClick={onSpnClear}>
                  <CloseIcon />
                </span>
              )}
            </div>
          </div>
        </span>

        <div className="popover">
          <img
            id="spnInfo"
            alt="InfoSpn"
            src={InfoIcon}
            ref={anchorSpn}
            className="popover-toggle"
          ></img>
          <div className="popover-options">
            <div className="popover-arrow-box">
              <img alt={`Spn-${language}`} src={infoSpnSrc} />
            </div>
          </div>
        </div>
      </div>

      <div className="search-box search-box-with-popover">
        <span className="search-with-options">
          <div className="site-search search-with-before-text">
            <span className="search-icon">
              <img alt="Search" src={SearchIcon}></img>
            </span>
            <div className="search-input">
              <span className="before-text">{t('fmi_fault')}</span>
              <Input
                id="searchFMI"
                name="searchFMI"
                value={fmiSearchValue}
                placeholder={t('search_fmi_fault_codes')}
                onChange={newValue => {
                  onFmiChange(newValue.value);
                }}
              />
              {fmiSearchValue && (
                <span className="search-icon-close" onClick={onFmiClear}>
                  <CloseIcon />
                </span>
              )}
            </div>
          </div>
        </span>

        <div className="popover">
          <img
            id="fmiInfo"
            alt="InfoFmi"
            src={InfoIcon}
            ref={anchorFmi}
            className="popover-toggle"
          ></img>
          <div className="popover-options">
            <div className="popover-arrow-box">
              <img alt={`Fmi-${language}`} src={infoFmiSrc} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
